import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import { useSurveyParams } from '../context/SurveyParamsContext'
import 'survey-core/i18n/traditional-chinese'
import {
  convertVisibleIf,
  convertQuestionMappings,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 以下皆為 1-based 寫法，convertVisibleIf 會自動 -1 還原為 0-based。
  // JSON q1-q4 為開始訪問時間，q5-q60 為訪員填寫的名冊/地址/障別欄位（A~E2），問卷本文自 q61 起。
  // PDF 題號 ↔ JSON 題號：1=q62、2a/2b=q63/q64、3=q65、4=q66、5=q67、6=q68、
  //   7-1/7-2=q69/q70、8=q71、8a/8b=q72/q73、9=q74、9a-9d=q75-q78、10a/10b=q79/q80、
  //   11=q81、12=q82、12a=q83、13=q85、14-20=q86-q95（18a=q91、19=q92、19-1=q93、19a=q94）、
  //   21-27=q96-q104、28=q105、29=q106、30=q108、31=q109、32=q110、33-1~3=q111-q113、
  //   34=q114、35A-E=q115-q124（每組「有沒有搭乘」+「困難」）、36/36a=q125/q126、
  //   37/37a=q127/q128、38/39=q130/q131、40=q132、40a=q133、41=q134、41O=q135、
  //   41a1=q136（繼承 41 的勾選項）、41a2=q137（完整 22 項代碼，跳題判斷用這題）、
  //   42=q138-q145、43-51=q146-q161、52=q163、53=q164-q166、54=q167、55A-D=q168-q171、
  //   56=q172、57=q173、58=q174、59/59a=q175/q176、60=q178、60a=q179、60b=q180、
  //   60b-2=q181、60c=q182、61=q183、62=q184、63=q186、63a/63b=q187/q188、64=q189、
  //   64a/64b=q190/q191、65/65a=q192/q193、66=q194、66a/66b=q195/q196、67/67a=q197/q198、
  //   68=q199、68a=q200、68b=q201、68bO=q202、68c=q203、69=q204、70=q205、
  //   70a-70c=q206-q208、71=q209、72/72a=q210/q211、73-1=q213、73-2=q214、
  //   73-2a~d=q215-q218、74/74a=q219/q220、75-1=q221、75-2=q222、75-2a~d=q223-q226、
  //   76/76a=q227/q228、77=q229、78=q230、79=q231、80=q232、81=q233、
  //   受訪者生理性別(訪員依名冊勾選)=q234、82=q235、82a-82c=q236-q238、
  //   F=q239、F1-F3=q240-q242。

  // A題 (2)與名冊地址不同 → 才問 A1-A3 戶籍地址題組(q6-q29)
  const registeredDiffers = '{q5} = 2'
  // B題 (2)與戶籍地址不同 → 才問 B1-B3 居住地址題組(q31-q54)
  const livesElsewhere = '{q30} = 2'

  // E1 舊制障礙類別(q59) 第9項＝植物人：第12題、陸章工作現況免填答，且第72題後結束訪查
  const notPlantPerson = '{q59} <> 9'
  // 2a題 出生年(q63)：第1項是民國0年(115歲)、第116項是民國115年(0歲)，
  // 故「足歲年齡 = 116 - 選項序號」，年齡門檻一律換算成序號區間。
  const isAge3AndUp = '{q63} <= 113' // 3歲以上（未滿3歲＝114-116項）
  const isAge3To5 = '({q63} >= 111 and {q63} <= 113)' // 3歲至未滿6歲
  const isAge6AndUp = '{q63} <= 110' // 6歲以上
  const isAge12AndUp = '{q63} <= 104' // 12歲及以上
  const isAge15AndUp = '{q63} <= 101' // 15歲以上（工作現況）
  const isAge25To74 = '({q63} >= 42 and {q63} <= 91)' // 25歲至未滿75歲（癌症篩檢）
  // 76題 限民國93年1月13日前(含當日)出生；JSON 只到「月」，93年1月整月視為符合
  const bornBeforeElectionDay = '(({q63} <= 93) or ({q63} = 94 and {q64} = 1))'
  // 13題 居住地點：(1)一般住宅續問 14-20 題；(2)住宿式機構續問 21-27 題
  const livesAtHome = '{q85} = 1'
  const livesInFacility = '{q85} = 2'
  // 30題 (5)都沒有外出者，31-33 題免答
  const goesOut = '({q108} = 1 or {q108} = 2 or {q108} = 3 or {q108} = 4)'
  // 40題 (1)可以獨立自我照顧 → 跳問 52 題，故 40a-51 題都要「非(1)」
  const needsCare = '({q132} = 2 or {q132} = 3)'
  // 43-51 題另限「住一般住宅者填答」
  const homeServiceSection = '(' + livesAtHome + ') and ' + needsCare
  // 陸章 60-62 題：15歲以下、障礙類別為植物人者免填答
  const workSection = '(' + notPlantPerson + ') and (' + isAge15AndUp + ')'
  // 19題 12 項設施任一選「(2)需要」→ 續問 19a
  const needsAnyFacility =
    '({q92.1} = 2) or ({q92.2} = 2) or ({q92.3} = 2) or ({q92.4} = 2) or ' +
    '({q92.5} = 2) or ({q92.6} = 2) or ({q92.7} = 2) or ({q92.8} = 2) or ' +
    '({q92.9} = 2) or ({q92.10} = 2) or ({q92.11} = 2) or ({q92.12} = 2)'
  // 41a2(q137) 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
  // 判斷用 41a2 而非繼承題 41a1(q136)：繼承來的 value 會被重新編號，索引不可靠
  const mainCarerIsFamily = '({q137} >= 1 and {q137} <= 12)'
  // 74題 73-1 四項休閒活動全部「(2)沒有」者不用回答（任一項有參加才問）
  const joinedAnyLeisure =
    '({q213.1} = 1) or ({q213.2} = 1) or ({q213.3} = 1) or ({q213.4} = 1)'

  const visibleIfConditionsInit = convertVisibleIf({
    // A1-A3. 限 A 題選(2)與名冊地址不同者填答
    q6: registeredDiffers,
    q29: registeredDiffers,
    // A2. 戶籍地址_鄉鎮市區：22 題各對應一個縣市，只顯示 A1(q6) 所選縣市的那一題
    q7: registeredDiffers + ' and ({q6} = 1)', // 新北市
    q8: registeredDiffers + ' and ({q6} = 2)', // 臺北市
    q9: registeredDiffers + ' and ({q6} = 3)', // 基隆市
    q10: registeredDiffers + ' and ({q6} = 4)', // 桃園市
    q11: registeredDiffers + ' and ({q6} = 5)', // 新竹市
    q12: registeredDiffers + ' and ({q6} = 6)', // 新竹縣
    q13: registeredDiffers + ' and ({q6} = 7)', // 苗栗縣
    q14: registeredDiffers + ' and ({q6} = 8)', // 臺中市
    q15: registeredDiffers + ' and ({q6} = 9)', // 彰化縣
    q16: registeredDiffers + ' and ({q6} = 10)', // 南投縣
    q17: registeredDiffers + ' and ({q6} = 11)', // 雲林縣
    q18: registeredDiffers + ' and ({q6} = 12)', // 嘉義市
    q19: registeredDiffers + ' and ({q6} = 13)', // 嘉義縣
    q20: registeredDiffers + ' and ({q6} = 14)', // 臺南市
    q21: registeredDiffers + ' and ({q6} = 15)', // 高雄市
    q22: registeredDiffers + ' and ({q6} = 16)', // 屏東縣
    q23: registeredDiffers + ' and ({q6} = 17)', // 宜蘭縣
    q24: registeredDiffers + ' and ({q6} = 18)', // 花蓮縣
    q25: registeredDiffers + ' and ({q6} = 19)', // 臺東縣
    q26: registeredDiffers + ' and ({q6} = 20)', // 澎湖縣
    q27: registeredDiffers + ' and ({q6} = 21)', // 金門縣
    q28: registeredDiffers + ' and ({q6} = 22)', // 連江縣
    // B1-B3. 限 B 題選(2)與戶籍地址不同者填答
    q31: livesElsewhere,
    q54: livesElsewhere,
    // B2. 居住地址_鄉鎮市區：同理，對應 B1(q31) 所選縣市，並限 B 題選(2)
    q32: livesElsewhere + ' and ({q31} = 1)', // 新北市
    q33: livesElsewhere + ' and ({q31} = 2)', // 臺北市
    q34: livesElsewhere + ' and ({q31} = 3)', // 基隆市
    q35: livesElsewhere + ' and ({q31} = 4)', // 桃園市
    q36: livesElsewhere + ' and ({q31} = 5)', // 新竹市
    q37: livesElsewhere + ' and ({q31} = 6)', // 新竹縣
    q38: livesElsewhere + ' and ({q31} = 7)', // 苗栗縣
    q39: livesElsewhere + ' and ({q31} = 8)', // 臺中市
    q40: livesElsewhere + ' and ({q31} = 9)', // 彰化縣
    q41: livesElsewhere + ' and ({q31} = 10)', // 南投縣
    q42: livesElsewhere + ' and ({q31} = 11)', // 雲林縣
    q43: livesElsewhere + ' and ({q31} = 12)', // 嘉義市
    q44: livesElsewhere + ' and ({q31} = 13)', // 嘉義縣
    q45: livesElsewhere + ' and ({q31} = 14)', // 臺南市
    q46: livesElsewhere + ' and ({q31} = 15)', // 高雄市
    q47: livesElsewhere + ' and ({q31} = 16)', // 屏東縣
    q48: livesElsewhere + ' and ({q31} = 17)', // 宜蘭縣
    q49: livesElsewhere + ' and ({q31} = 18)', // 花蓮縣
    q50: livesElsewhere + ' and ({q31} = 19)', // 臺東縣
    q51: livesElsewhere + ' and ({q31} = 20)', // 澎湖縣
    q52: livesElsewhere + ' and ({q31} = 21)', // 金門縣
    q53: livesElsewhere + ' and ({q31} = 22)', // 連江縣
    // 7. 未滿3歲不用填答；3歲至未滿6歲填學齡前兩項(q69)，6歲以上填(3)~(10)(q70)
    q69: isAge3To5,
    q70: isAge6AndUp,
    // 8. (2)有配偶/(3)與人同居 → 續問 8a、8b；(1)未婚(4)離婚(5)分居(6)喪偶跳問第 9 題
    q72: '{q71} = 2 or {q71} = 3',
    q73: '{q71} = 2 or {q71} = 3',
    // 9. (2)有子女 → 續問 9a-9d 四個人數欄
    q75: '{q74} = 2',
    q76: '{q74} = 2',
    q77: '{q74} = 2',
    q78: '{q74} = 2',
    // 12. 3歲以下、障礙類別為植物人者免填答，跳問第 13 題
    //     （PDF 註記寫「跳問11題」為筆誤，PM 確認應為第 13 題）
    q82: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    q83: '(' + notPlantPerson + ') and (' + isAge3AndUp + ')',
    // 14-20. 限 13 題選(1)一般住宅
    q86: livesAtHome,
    q87: livesAtHome,
    q88: livesAtHome,
    q89: livesAtHome,
    q90: livesAtHome,
    // 18a. 18 題非「(1)獨居」者才問同住人員；第 11 項是 o_type 6，值為 "other"
    q91:
      '(' +
      livesAtHome +
      ') and (({q90} >= 2 and {q90} <= 10) or ({q90} = "other"))',
    q92: livesAtHome,
    // 19-1. 19 題第 12 列「其他」需要時，才問是哪種設施
    q93: '(' + livesAtHome + ') and ({q92.12} = 2)',
    // 19a. 逐列對應 19 題：該列選「(2)需要」，19a 才出現同一列
    q94: '(' + livesAtHome + ') and (' + needsAnyFacility + ')',
    'q94.row1': '{q92.1} = 2',
    'q94.row2': '{q92.2} = 2',
    'q94.row3': '{q92.3} = 2',
    'q94.row4': '{q92.4} = 2',
    'q94.row5': '{q92.5} = 2',
    'q94.row6': '{q92.6} = 2',
    'q94.row7': '{q92.7} = 2',
    'q94.row8': '{q92.8} = 2',
    'q94.row9': '{q92.9} = 2',
    'q94.row10': '{q92.10} = 2',
    'q94.row11': '{q92.11} = 2',
    'q94.row12': '{q92.12} = 2',
    q95: livesAtHome,
    // 21-27. 限 13 題選(2)住宿式機構
    q96: livesInFacility,
    // 22. 居住的機構類型：PM 要求隱藏，一律不顯示
    q97: 'false',
    q98: livesInFacility,
    // 23. (1)沒有家人或親屬 → 跳問第 25 題；(2)有 → 續問 23a、24
    q99: '(' + livesInFacility + ') and ({q98} = 2)',
    q100: '(' + livesInFacility + ') and ({q98} = 2)',
    q101: livesInFacility,
    q102: livesInFacility,
    q103: livesInFacility,
    q104: livesInFacility,
    // 31-33. 30 題選(5)都沒有外出者免答
    q109: goesOut,
    q110: goesOut,
    q111: goesOut,
    // 33-1 選第16項「(95)沒有使用交通工具」→ 33-2、33-3 都不顯示
    q112: goesOut + ' and ({q111} <> 16)',
    // 33-2 選第16項「(95)沒有次要選項」→ 33-3 不顯示
    q113: goesOut + ' and ({q111} <> 16) and ({q112} <> 16)',
    // 34. 限 30 題選(4)很少外出或(5)都沒有外出
    q114: '{q108} = 4 or {q108} = 5',
    // 35A-E. 各組先問「(01)有搭乘」，有搭乘才問困難題
    q116: '{q115} = 1',
    q118: '{q117} = 1',
    q120: '{q119} = 1',
    q122: '{q121} = 1',
    q124: '{q123} = 1',
    // 36. (1)有使用通用計程車 → 續問 36a
    q126: '{q125} = 1',
    // 37. (1)有申請復康巴士 → 續問 37a
    q128: '{q127} = 1',
    // 38、39. 未滿6歲者免答，跳問第 40 題
    q130: isAge6AndUp,
    q131: isAge6AndUp,
    // 40. (1)可以獨立自我照顧 → 跳問第 52 題
    q133: needsCare,
    q134: needsCare,
    // 41O. 41 題勾選(12)其他親戚 → 續問說明（該選項 o_type 為 0，用數字索引）
    q135: needsCare + ' and ({q134} anyof [12])',
    q136: needsCare,
    q137: needsCare,
    // 42. 41a 填(1)~(12)家人代碼者續答 42 題；填(13)~(22)者跳答 43 題
    q138: needsCare + ' and ' + mainCarerIsFamily,
    q139: needsCare + ' and ' + mainCarerIsFamily,
    q140: needsCare + ' and ' + mainCarerIsFamily,
    q141: needsCare + ' and ' + mainCarerIsFamily,
    q142: needsCare + ' and ' + mainCarerIsFamily,
    q143: needsCare + ' and ' + mainCarerIsFamily,
    q144: needsCare + ' and ' + mainCarerIsFamily,
    q145: needsCare + ' and ' + mainCarerIsFamily,
    // 43-51. 住一般住宅者填答，未住一般住宅者跳問第 52 題
    q146: homeServiceSection,
    // 43. (1)不需要居家服務 → 跳問第 46 題（43a-43d、44、45 皆不問）
    q147: homeServiceSection + ' and ({q146} = 2)',
    q148: homeServiceSection + ' and ({q146} = 2)',
    q149: homeServiceSection + ' and ({q146} = 2)',
    q150: homeServiceSection + ' and ({q146} = 2)',
    q151: homeServiceSection + ' and ({q146} = 2)',
    // 44. (1)有申請且符合需要 → 跳問第 46 題，故第 45 題限(2)(3)
    q152:
      homeServiceSection + ' and ({q146} = 2) and ({q151} = 2 or {q151} = 3)',
    q153: homeServiceSection,
    // 46. (1)不需要日間服務 → 跳問第 49 題（46a-46c、47、48 皆不問）
    q154: homeServiceSection + ' and ({q153} = 2)',
    q155: homeServiceSection + ' and ({q153} = 2)',
    q156: homeServiceSection + ' and ({q153} = 2)',
    q157: homeServiceSection + ' and ({q153} = 2)',
    // 47. (1)有申請且符合需要 → 跳問第 49 題，故第 48 題限(2)(3)
    q158:
      homeServiceSection + ' and ({q153} = 2) and ({q157} = 2 or {q157} = 3)',
    q159: homeServiceSection,
    // 49. (1)有申請且符合需要 → 跳問第 51 題，故第 50 題限(2)(3)
    //     選第4項「(90)身障者本人無法回答」→ 第 50、51 題都不顯示
    q160: homeServiceSection + ' and ({q159} = 2 or {q159} = 3)',
    q161: homeServiceSection + ' and ({q159} <> 4)',
    // 53. 次要收入來源選第15項「(95)無次要收入來源」→ 不問再次要收入來源
    q166: '{q165} <> 15',
    // 陸、工作狀況 章節標題：整章(60-62 題)都被隱藏時，標題也不顯示
    q177: workSection,
    // 60-62. 15歲以下、障礙類別為植物人者免填答，跳問第 63 題
    q178: workSection,
    // 60. (1)有在做工作 → 續問 60a，答完跳問第 62 題
    q179: workSection + ' and ({q178} = 1)',
    // 60. (2)無工作且在找工作 → 續問 60b
    q180: workSection + ' and ({q178} = 2)',
    // 60b. (2)不能立刻開始工作 → 續問原因，答完跳問第 63 題
    q181: workSection + ' and ({q178} = 2) and ({q180} = 2)',
    // 60. (4)沒有工作且未找工作 → 續問 60c，答完跳問第 63 題
    q182: workSection + ' and ({q178} = 4)',
    // 61. 60 題選(3)已找到工作，或選(2)且 60b 選(1)能立刻開始工作者才問
    q183:
      workSection + ' and (({q178} = 3) or (({q178} = 2) and ({q180} = 1)))',
    // 62. 只有 60 題選(1)有在做工作者作答
    q184: workSection + ' and ({q178} = 1)',
    // 63. (1)有做 → 63a；(2)沒有做過 → 63b；(98)不知道 → 跳問第 64 題
    q187: '{q186} = 1',
    q188: '{q186} = 2',
    // 64. 未滿25歲或75歲以上者免答，跳問第 65 題
    q189: isAge25To74,
    // 64. (1)有做 → 64a；(2)沒有做過 → 64b；(98)不知道 → 跳問第 65 題
    q190: isAge25To74 + ' and ({q189} = 1)',
    q191: isAge25To74 + ' and ({q189} = 2)',
    // 65. (1)需要定期就醫 → 續問 65a；(2)不需要 → 跳問第 66 題
    q193: '{q192} = 1',
    // 66. (1)需要復健治療 → 續問 66a、66b；(2)不需要 → 跳問第 67 題
    q195: '{q194} = 1',
    q196: '{q194} = 1',
    // 67. (2)診所 → 續問 67a
    q198: '{q197} = 2',
    // 68. (1)完全沒有困難 → 跳問第 69 題；(2)有困難 → 續問 68a、68b
    q200: '{q199} = 2',
    q201: '{q199} = 2',
    // 68bO. 68b 勾(6)缺乏與醫護人員溝通的支持服務 → 續問說明
    q202: '({q199} = 2) and ({q201} anyof [6])',
    // 68c. 68b 勾(3)醫院機構內缺乏無障礙環境 → 續問缺乏項目
    q203: '({q199} = 2) and ({q201} anyof [3])',
    // 70. (1)需要且有使用 → 70a；(2)需要但未使用 → 70b、70c
    q206: '{q205} = 1',
    q207: '{q205} = 2',
    q208: '{q205} = 2',
    // 72. (4)不好或(5)很不好 → 續問 72a
    q211: '{q210} = 4 or {q210} = 5',
    // 捌、休閒、社會參與及自我決定 章節標題：整章(73-82 題)都被隱藏時，標題也不顯示
    q212: notPlantPerson,
    // 73 題以後：障礙類別為植物人者於第 72 題後結束訪查
    q213: notPlantPerson,
    q214: notPlantPerson,
    // 73-2a~73-2d. 73-2 該列選「(4)無」(第 2 欄) → 續問該活動不想參與的主要原因
    q215: '(' + notPlantPerson + ') and ({q214.1} = 2)',
    q216: '(' + notPlantPerson + ') and ({q214.2} = 2)',
    q217: '(' + notPlantPerson + ') and ({q214.3} = 2)',
    q218: '(' + notPlantPerson + ') and ({q214.4} = 2)',
    // 74. 73 題完全沒有參與休閒活動者不用回答
    q219: '(' + notPlantPerson + ') and (' + joinedAnyLeisure + ')',
    // 74a. (2)有遭遇困難 → 續問哪些困難
    q220:
      '(' +
      notPlantPerson +
      ') and (' +
      joinedAnyLeisure +
      ') and ({q219} = 2)',
    q221: notPlantPerson,
    q222: notPlantPerson,
    // 75-2a~75-2d. 75-2 該列選「(2)沒有」→ 續問該社團不想參與的主要原因
    q223: '(' + notPlantPerson + ') and ({q222.1} = 2)',
    q224: '(' + notPlantPerson + ') and ({q222.2} = 2)',
    q225: '(' + notPlantPerson + ') and ({q222.3} = 2)',
    q226: '(' + notPlantPerson + ') and ({q222.4} = 2)',
    // 76. 限民國93年1月13日前(含當日)出生者作答
    q227: '(' + notPlantPerson + ') and ' + bornBeforeElectionDay,
    // 76. (2)沒有去投票 → 續問 76a
    q228: '(' + notPlantPerson + ') and ({q227} = 2)',
    // 77. (3)沒有投票權者跳問第 78 題
    q229: '(' + notPlantPerson + ') and ({q227} = 1 or {q227} = 2)',
    // 78. 限12歲及以上作答
    q230: '(' + notPlantPerson + ') and (' + isAge12AndUp + ')',
    q231: notPlantPerson,
    q232: notPlantPerson,
    q233: notPlantPerson,
    // 受訪者生理性別：僅供 82a-82c 跳題判斷，隨 82 題一起受「植物人結束訪查」限制
    q234: notPlantPerson,
    // 82. (2)不願意回答 → 82a-82c 皆不顯示，問卷自然結束
    q235: notPlantPerson,
    // 82a. 受訪者生理性別(q234)選(2)女性才顯示
    q236: '(' + notPlantPerson + ') and ({q235} = 1) and ({q234} = 2)',
    // 82b. 男性直接作答；女性限 82a 選(2)沒有（選(1)有則跳 82c）
    q237:
      '(' +
      notPlantPerson +
      ') and ({q235} = 1) and (({q234} = 1) or ({q236} = 2))',
    // 82c. 82a 或 82b 任一選(1)有
    q238:
      '(' +
      notPlantPerson +
      ') and ({q235} = 1) and (({q236} = 1) or ({q237} = 1))',
    // F. (3)大部分非身心障礙者本人回答 → 續問 F1~F3
    // 訪員填寫欄位不受「植物人結束訪查」影響，一律作答
    q240: '{q239} = 3',
    q241: '{q239} = 3',
    q242: '{q239} = 3',
  })
  // 章節標題頁（壹~捌）無選項，以及訪員填寫欄位，一律非必答
  const notRequired = convertNotRequired([
    57, // D2.聯絡電話
    58, // D2.手機號碼
    61, // 壹、基本資料
    84, // 貳、居住狀況
    107, // 參、外出及交通狀況
    129, // 肆、起居生活狀況
    162, // 伍、經濟狀況
    177, // 陸、工作狀況
    185, // 柒、健康及醫療照顧
    212, // 捌、休閒、社會參與及自我決定
    230, // 78.是否可以自己決定下列事項（限身心障礙者本人回答）
    243, // 意見欄
    244, // 可接受複查時段
  ])
  const mappedRateChoice = null
  const pageBreakQuestions = null
  const triggersConditionsInit = null
  // 帶出來源(前題) → 帶入目標(後題)：後題選項＝前題已勾選的選項
  const questionMappings = convertQuestionMappings({
    q82: 'q83', // 12.溝通方式(複選) → 12a.以上最主要溝通方式
    q134: 'q136', // 41.提供協助或照顧的人(複選) → 41a1.其中最主要的照顧者
  })

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
    sfdSamplerId,
    sfdPId,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
    sfdSamplerId,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 本案為政府委託訪問調查，結語採客戶指定文案，不使用 EZChoice 預設結語
  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>請致贈受訪者禮品並留下受訪說明單。<br>若有主要家庭照顧者，請於受訪說明單留下樣本編號，並邀請主要家庭照顧者填寫B表問卷。</div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    enableProgressSaving:
      requiresLogin.login || webAuthType > 0 || !!sfdSamplerId,
    webAuthType,
    sfdSamplerId,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
